import React, { useState } from "react";
import { toast } from "sonner";
import { Lock, Stamp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ApprovalPanel } from "@/components/platform/ApprovalPanel";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { Panel } from "@/components/rights/RightsBadges";

export const ClosurePanel = ({ r, canEdit, onChanged }) => {
  const auth = useAuth();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const latest = r.approvals[0];
  const blockers = r.checklist.gates.APPROVAL;
  const pending = latest?.status === "Pending";
  const ready = latest?.status === "Approved" && r.work_item?.status === "COMPLETED";

  const run = async (path, body, msg) => {
    setBusy(true);
    try {
      await api.post(`/rights/${r.id}/${path}`, body);
      toast.success(msg);
      setNote("");
      onChanged();
    } catch (e) { toast.error(apiError(e, "Action failed")); } finally { setBusy(false); }
  };

  return (
    <Panel eyebrow="Approval engine" title="Closure approval" testId="closure-panel">
      {r.status === "CLOSED" ? (
        <div className="flex items-center gap-2 text-sm text-slate-700" data-testid="closure-closed">
          <Lock className="w-4 h-4 text-slate-500" /> Closed on {r.closed_at} {r.closed_on_time ? "(on time)" : "(late)"}
        </div>
      ) : r.status !== "RESPOND" ? (
        <div className="text-xs text-slate-500" data-testid="closure-not-ready">Available in the Respond stage.</div>
      ) : (
        <div className="space-y-3">
          {!pending && !ready && blockers.length > 0 && (
            <ul className="text-xs text-amber-700 space-y-0.5" data-testid="closure-blockers">{blockers.map((b) => <li key={b}>• {b}</li>)}</ul>
          )}
          {canEdit && !pending && !ready && (
            <>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Summary for approver (optional)"
                        className="text-xs" data-testid="closure-note" />
              <Button size="sm" disabled={busy || blockers.length > 0} className="w-full bg-[#003366] hover:bg-[#003366]/90"
                      onClick={() => run("closure-approval", { note }, "Closure approval requested")} data-testid="closure-request-button">
                <Stamp className="w-3.5 h-3.5 mr-1" /> Request closure approval
              </Button>
              <div className="text-[10px] text-slate-500">Segregation of duties: another approver must approve the closure.</div>
            </>
          )}
          {ready && canEdit && (
            <Button size="sm" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => run("close", { comment: note }, "Request closed")} data-testid="closure-close-button">
              <Lock className="w-3.5 h-3.5 mr-1" /> Close request
            </Button>
          )}
        </div>
      )}
      {latest && (
        <div className="mt-4">
          <ApprovalPanel approval={latest} currentUser={auth.user} onChanged={onChanged} />
        </div>
      )}
    </Panel>
  );
};
